import React, { useState, useEffect, useRef, useCallback } from 'react';
import { searchUsers, type UserSearchResult } from '../services/userSearchService';

// ---------------------------------------------------------------------------
// Unicode rich-text helpers
// Works on all platforms (LinkedIn, X, Instagram, etc.) — no HTML needed.
// ---------------------------------------------------------------------------
const BOLD: Record<string, string> = {
  A:'𝗔',B:'𝗕',C:'𝗖',D:'𝗗',E:'𝗘',F:'𝗙',G:'𝗚',H:'𝗛',I:'𝗜',J:'𝗝',K:'𝗞',L:'𝗟',M:'𝗠',
  N:'𝗡',O:'𝗢',P:'𝗣',Q:'𝗤',R:'𝗥',S:'𝗦',T:'𝗧',U:'𝗨',V:'𝗩',W:'𝗪',X:'𝗫',Y:'𝗬',Z:'𝗭',
  a:'𝗮',b:'𝗯',c:'𝗰',d:'𝗱',e:'𝗲',f:'𝗳',g:'𝗴',h:'𝗵',i:'𝗶',j:'𝗷',k:'𝗸',l:'𝗹',m:'𝗺',
  n:'𝗻',o:'𝗼',p:'𝗽',q:'𝗾',r:'𝗿',s:'𝘀',t:'𝘁',u:'𝘂',v:'𝘃',w:'𝘄',x:'𝘅',y:'𝘆',z:'𝘇',
  '0':'𝟬','1':'𝟭','2':'𝟮','3':'𝟯','4':'𝟰','5':'𝟱','6':'𝟲','7':'𝟳','8':'𝟴','9':'𝟵',
};
const ITALIC: Record<string, string> = {
  A:'𝘈',B:'𝘉',C:'𝘊',D:'𝘋',E:'𝘌',F:'𝘍',G:'𝘎',H:'𝘏',I:'𝘐',J:'𝘑',K:'𝘒',L:'𝘓',M:'𝘔',
  N:'𝘕',O:'𝘖',P:'𝘗',Q:'𝘘',R:'𝘙',S:'𝘚',T:'𝘛',U:'𝘜',V:'𝘝',W:'𝘞',X:'𝘟',Y:'𝘠',Z:'𝘡',
  a:'𝘢',b:'𝘣',c:'𝘤',d:'𝘥',e:'𝘦',f:'𝘧',g:'𝘨',h:'𝘩',i:'𝘪',j:'𝘫',k:'𝘬',l:'𝘭',m:'𝘮',
  n:'𝘯',o:'𝘰',p:'𝘱',q:'𝘲',r:'𝘳',s:'𝘴',t:'𝘵',u:'𝘶',v:'𝘷',w:'𝘸',x:'𝘹',y:'𝘺',z:'𝘻',
};

const applyMap = (text: string, map: Record<string, string>) =>
  Array.from(text).map((ch) => map[ch] ?? ch).join('');

// Detect if a string is already fully in the target map
const isAlreadyFormatted = (text: string, map: Record<string, string>) => {
  const values = new Set(Object.values(map));
  return Array.from(text).every((ch) => values.has(ch) || !/[a-zA-Z0-9]/.test(ch));
};

// Toggle: apply if not already formatted, remove if it is
const BOLD_VALUES = new Set(Object.values(BOLD));
const ITALIC_VALUES = new Set(Object.values(ITALIC));
const BOLD_REVERSE = Object.fromEntries(Object.entries(BOLD).map(([k, v]) => [v, k]));
const ITALIC_REVERSE = Object.fromEntries(Object.entries(ITALIC).map(([k, v]) => [v, k]));

const toggleFormat = (text: string, map: Record<string, string>, reverseMap: Record<string, string>) => {
  if (isAlreadyFormatted(text, map)) {
    return Array.from(text).map((ch) => reverseMap[ch] ?? ch).join('');
  }
  return applyMap(text, map);
};

interface MentionInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  className?: string;
  style?: React.CSSProperties;
  showFormatting?: boolean;
  onMentionsChange?: (mentions: { userIds: string[]; names: string[] }) => void;
  onFocus?: () => void;
  onPaste?: (event: React.ClipboardEvent<HTMLTextAreaElement>) => void;
}

export const MentionInput: React.FC<MentionInputProps> = ({
  value,
  onChange,
  placeholder = 'Deel iets waardevols met ondernemers...',
  rows = 2,
  className = '',
  style,
  showFormatting = false,
  onMentionsChange,
  onFocus,
  onPaste
}) => {
  // Saved selection so formatting buttons work on mobile (blur clears selection)
  const [savedSelection, setSavedSelection] = useState<{ start: number; end: number } | null>(null);
  const [hasSelection, setHasSelection] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<UserSearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionStart, setMentionStart] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [mentionMap, setMentionMap] = useState<Map<string, { userId: string; displayName: string }>>(new Map());
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        !suggestionsRef.current?.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
        setMentionStart(null);
        setSearchTerm('');
      }
    };

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showSuggestions]);

  // Extract current mention search term
  const extractMentionSearch = useCallback((text: string, cursorPos: number): { start: number; term: string } | null => {
    const textBeforeCursor = text.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex === -1) return null;
    
    // Check if there's a space or newline between @ and cursor (invalid mention)
    const afterAt = textBeforeCursor.substring(lastAtIndex + 1);
    const hasSpaceBeforeCursor = afterAt.includes(' ') || afterAt.includes('\n');
    
    // Allow empty term (just @) to show suggestions
    if (hasSpaceBeforeCursor) return null;
    
    return {
      start: lastAtIndex,
      term: afterAt.trim()
    };
  }, []);

  // Mock suggestions for demo
  const getMockSuggestions = (): UserSearchResult[] => [
    {
      userId: 'mock1',
      displayName: 'Demo Gebruiker 1',
      firstName: 'Demo',
      lastName: 'Gebruiker',
      email: 'demo1@example.com',
      photoURL: 'https://ui-avatars.com/api/?name=Demo+Gebruiker&background=4F6660&color=fff',
      companyName: 'Demo Bedrijf'
    },
    {
      userId: 'mock2',
      displayName: 'Demo Gebruiker 2',
      firstName: 'Demo',
      lastName: 'Gebruiker2',
      email: 'demo2@example.com',
      photoURL: 'https://ui-avatars.com/api/?name=Demo+Gebruiker2&background=0A2342&color=fff',
      companyName: 'Demo Bedrijf 2'
    },
    {
      userId: 'mock3',
      displayName: 'Demo Gebruiker 3',
      firstName: 'Demo',
      lastName: 'Gebruiker3',
      email: 'demo3@example.com',
      photoURL: 'https://ui-avatars.com/api/?name=Demo+Gebruiker3&background=4F6660&color=fff',
      companyName: 'Demo Bedrijf 3'
    }
  ];

  // Search for users when @ is typed
  useEffect(() => {
    const searchUsersAsync = async () => {
      // If @ is detected, show suggestions
      if (mentionStart !== null) {
        if (searchTerm.length >= 2) {
          // Search with term (min 2 chars for real search)
          const results = await searchUsers(searchTerm, 8);
          if (results.length > 0) {
            setSuggestions(results);
            setShowSuggestions(true);
          } else {
            // No results, show mock
            setSuggestions(getMockSuggestions());
            setShowSuggestions(true);
          }
        } else {
          // Empty or 1 char - show mock suggestions immediately
          setSuggestions(getMockSuggestions());
          setShowSuggestions(true);
        }
        setSelectedIndex(0);
      } else {
        // No @ detected, hide suggestions
        setSuggestions([]);
        setShowSuggestions(false);
      }
    };

    const timeoutId = setTimeout(searchUsersAsync, 150); // Fast response
    return () => clearTimeout(timeoutId);
  }, [searchTerm, mentionStart]);

  // Handle text change
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart || newValue.length;
    
    onChange(newValue);
    
    // Check for @ mention
    const mentionInfo = extractMentionSearch(newValue, cursorPos);
    
    if (mentionInfo) {
      setMentionStart(mentionInfo.start);
      setSearchTerm(mentionInfo.term);
    } else {
      setShowSuggestions(false);
      setMentionStart(null);
      setSearchTerm('');
    }
  };

  // Handle cursor position changes (for @ detection)
  const handleKeyUp = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!textareaRef.current) return;
    const cursorPos = textareaRef.current.selectionStart || 0;
    const mentionInfo = extractMentionSearch(value, cursorPos);
    
    if (mentionInfo) {
      setMentionStart(mentionInfo.start);
      setSearchTerm(mentionInfo.term);
    } else {
      setShowSuggestions(false);
      setMentionStart(null);
      setSearchTerm('');
    }
  };

  // Handle click to detect @ mentions
  const handleClick = () => {
    if (!textareaRef.current) return;
    setTimeout(() => {
      const cursorPos = textareaRef.current?.selectionStart || 0;
      const mentionInfo = extractMentionSearch(value, cursorPos);
      
      if (mentionInfo) {
        setMentionStart(mentionInfo.start);
        setSearchTerm(mentionInfo.term);
      } else {
        setShowSuggestions(false);
        setMentionStart(null);
        setSearchTerm('');
      }
    }, 0);
  };

  // Handle key events
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % suggestions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + suggestions.length) % suggestions.length);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        selectMention(suggestions[selectedIndex]);
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
      }
    }
  };

  // Select a mention
  const selectMention = (user: UserSearchResult) => {
    console.log('🎯 Selecting mention:', user);
    
    if (!textareaRef.current) {
      console.warn('⚠️ No textarea ref');
      return;
    }

    // If mentionStart is null, try to find the last @
    let currentMentionStart = mentionStart;
    if (currentMentionStart === null) {
      const currentCursorPos = textareaRef.current.selectionStart || value.length;
      const textBeforeCursor = value.substring(0, currentCursorPos);
      const lastAtIndex = textBeforeCursor.lastIndexOf('@');
      if (lastAtIndex !== -1) {
        currentMentionStart = lastAtIndex;
      } else {
        console.warn('⚠️ No @ found');
        return;
      }
    }

    const currentCursorPos = textareaRef.current.selectionStart || value.length;
    
    // Find where the @ mention ends (space, newline, or end of text)
    let mentionEnd = currentCursorPos;
    for (let i = currentMentionStart + 1; i < value.length; i++) {
      if (value[i] === ' ' || value[i] === '\n' || value[i] === '@') {
        mentionEnd = i;
        break;
      }
    }
    
    const textBefore = value.substring(0, currentMentionStart);
    const textAfter = value.substring(mentionEnd);
    
    // Create a username identifier - use displayName without spaces for @ mention
    // This makes it easier to read and more recognizable
    let username = user.displayName.replace(/\s+/g, '').toLowerCase();
    
    // Alternative: use firstName + lastName if available (more readable)
    if (user.firstName && user.lastName) {
      username = `${user.firstName}${user.lastName}`.toLowerCase();
    } else if (user.firstName) {
      username = user.firstName.toLowerCase();
    }
    
    const newText = `${textBefore}@${username} ${textAfter}`;
    
    // Update text first
    onChange(newText);
    
    // Store mention mapping with both username and displayName for matching
    const newMap = new Map(mentionMap);
    newMap.set(username, { userId: user.userId, displayName: user.displayName });
    // Also store by displayName for better matching
    const displayNameKey = user.displayName.toLowerCase().replace(/\s+/g, '');
    newMap.set(displayNameKey, { userId: user.userId, displayName: user.displayName });
    setMentionMap(newMap);
    
    // Close dropdown after a small delay to ensure click is processed
    setTimeout(() => {
      setShowSuggestions(false);
      setMentionStart(null);
      setSearchTerm('');
      
      // Set cursor position after mention
      if (textareaRef.current) {
        const newCursorPos = textBefore.length + username.length + 2; // +2 for @ and space
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        textareaRef.current.focus();
      }
    }, 100);
  };

  // Update mentions when value changes
  useEffect(() => {
    if (onMentionsChange) {
      const mentionedUserIds: string[] = [];
      const mentionedUserNames: string[] = [];
      const seenIds = new Set<string>();

      // Match @username patterns (alphanumeric and some special chars)
      const mentionRegex = /@([a-zA-Z0-9_]+)/g;
      let match;
      
      while ((match = mentionRegex.exec(value)) !== null) {
        const username = match[1].toLowerCase();
        
        // Try to find in mentionMap
        let mentionInfo = mentionMap.get(username);
        
        // If not found, try by displayName
        if (!mentionInfo) {
          // Try all keys in mentionMap
          mentionMap.forEach((info, key) => {
            if (!mentionInfo && (key.toLowerCase() === username || info.displayName.toLowerCase().replace(/\s+/g, '') === username)) {
              mentionInfo = info;
            }
          });
        }
        
        if (mentionInfo && !seenIds.has(mentionInfo.userId)) {
          mentionedUserIds.push(mentionInfo.userId);
          mentionedUserNames.push(mentionInfo.displayName);
          seenIds.add(mentionInfo.userId);
        }
      }

      onMentionsChange({ userIds: mentionedUserIds, names: mentionedUserNames });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, mentionMap]);

  // Calculate suggestions position
  const getSuggestionsPosition = () => {
    if (!textareaRef.current || !mentionStart) return { top: 0, left: 0 };
    
    const textarea = textareaRef.current;
    const textBeforeMention = value.substring(0, mentionStart);
    
    // Create a temporary span to measure text width
    const span = document.createElement('span');
    span.style.visibility = 'hidden';
    span.style.position = 'absolute';
    span.style.whiteSpace = 'pre-wrap';
    span.style.font = window.getComputedStyle(textarea).font;
    span.style.padding = window.getComputedStyle(textarea).padding;
    span.textContent = textBeforeMention;
    document.body.appendChild(span);
    
    const rect = textarea.getBoundingClientRect();
    const spanRect = span.getBoundingClientRect();
    document.body.removeChild(span);
    
    return {
      top: rect.top + rect.height + 5,
      left: rect.left + (spanRect.width || 0)
    };
  };

  // Apply formatting — uses saved selection or falls back to word at cursor
  const applyFormatting = useCallback((type: 'bold' | 'italic' | 'divider') => {
    const el = textareaRef.current;
    if (!el) return;

    const liveStart = el.selectionStart;
    const liveEnd = el.selectionEnd;

    // Prefer live selection → saved selection → word under cursor
    let sel: { start: number; end: number } | null =
      liveStart !== liveEnd
        ? { start: liveStart, end: liveEnd }
        : savedSelection && savedSelection.start !== savedSelection.end
          ? savedSelection
          : null;

    if (!sel) {
      // Find the word at the current cursor position
      const cursor = liveStart;
      let wordStart = cursor;
      let wordEnd = cursor;
      while (wordStart > 0 && /[\wÀ-öø-ÿ]/.test(value[wordStart - 1])) wordStart--;
      while (wordEnd < value.length && /[\wÀ-öø-ÿ]/.test(value[wordEnd])) wordEnd++;
      if (wordStart < wordEnd) sel = { start: wordStart, end: wordEnd };
    }

    if (type === 'divider') {
      const pos = sel?.start ?? liveStart;
      const before = value.slice(0, pos);
      const after = value.slice(pos);
      const prefix = before.length > 0 && !before.endsWith('\n') ? '\n' : '';
      const suffix = after.length > 0 && !after.startsWith('\n') ? '\n' : '';
      const line = `${prefix}─────────────────${suffix}`;
      onChange(before + line + after);
      requestAnimationFrame(() => {
        el.focus();
        el.selectionStart = el.selectionEnd = pos + line.length;
      });
      return;
    }

    if (!sel || sel.start === sel.end) return;

    const selected = value.slice(sel.start, sel.end);
    const formatted = type === 'bold'
      ? toggleFormat(selected, BOLD, BOLD_REVERSE)
      : toggleFormat(selected, ITALIC, ITALIC_REVERSE);

    onChange(value.slice(0, sel.start) + formatted + value.slice(sel.end));
    setSavedSelection(null);
    setHasSelection(false);
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = sel!.start;
      el.selectionEnd = sel!.start + formatted.length;
    });
  }, [value, onChange, savedSelection]);

  return (
    <div ref={containerRef} className="relative">
      {showFormatting && (
        <div className="flex items-center gap-2 px-1 pb-2 border-b border-gray-100 mb-1">
          {([
            { type: 'bold' as const,    icon: '𝗕', label: 'Vet' },
            { type: 'italic' as const,  icon: '𝘐', label: 'Cursief' },
            { type: 'divider' as const, icon: '───', label: 'Lijn' },
          ]).map(({ type, icon, label }) => (
            <button
              key={type}
              type="button"
              onPointerDown={(e) => {
                e.preventDefault();   // prevent textarea blur / focus loss
                applyFormatting(type);
              }}
              className="flex flex-col items-center justify-center rounded-xl px-3 py-1.5 bg-gray-100 text-gray-600 select-none transition-colors active:bg-[#4F6660] active:text-white"
              style={{ touchAction: 'manipulation', WebkitUserSelect: 'none' }}
            >
              <span className={`${type === 'divider' ? 'text-xs tracking-widest' : 'text-sm font-bold'} leading-none`}>{icon}</span>
              <span className="text-[9px] leading-tight mt-0.5 opacity-70">{label}</span>
            </button>
          ))}
        </div>
      )}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => {
          e.target.style.height = 'auto';
          e.target.style.height = `${e.target.scrollHeight}px`;
          handleChange(e);
        }}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onClick={handleClick}
        onFocus={(e) => {
          e.target.style.height = 'auto';
          e.target.style.height = `${e.target.scrollHeight}px`;
          onFocus?.();
        }}
        onSelect={(e) => {
          const el = e.currentTarget;
          const start = el.selectionStart;
          const end = el.selectionEnd;
          if (start !== end) {
            setSavedSelection({ start, end });
            setHasSelection(true);
          } else {
            setHasSelection(false);
          }
        }}
        onMouseUp={(e) => {
          const el = e.currentTarget;
          if (el.selectionStart !== el.selectionEnd) {
            setSavedSelection({ start: el.selectionStart, end: el.selectionEnd });
            setHasSelection(true);
          }
        }}
        onBlur={(e) => {
          const el = e.currentTarget;
          if (el.selectionStart !== el.selectionEnd) {
            setSavedSelection({ start: el.selectionStart, end: el.selectionEnd });
            setHasSelection(true);
          }
        }}
        onPaste={onPaste}
        placeholder={placeholder}
        rows={rows}
        className={`w-full p-3 border-0 resize-none focus:outline-none text-gray-700 placeholder-gray-400 ${className}`}
        style={style}
      />
      
      {/* Mention Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && mentionStart !== null && (
        <div
          ref={suggestionsRef}
          className="absolute z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto"
          style={{
            top: `${getSuggestionsPosition().top}px`,
            left: `${getSuggestionsPosition().left}px`,
            minWidth: '250px',
            pointerEvents: 'auto'
          }}
          onMouseDown={(e) => {
            e.stopPropagation(); // Prevent click outside handler from firing
          }}
        >
          {suggestions.map((user, index) => (
            <div
              key={user.userId}
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent textarea from losing focus
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('🖱️ Clicked on user:', user);
                selectMention(user);
              }}
              className={`px-4 py-2 cursor-pointer hover:bg-gray-50 active:bg-blue-100 flex items-center space-x-3 transition-colors ${
                index === selectedIndex ? 'bg-blue-50' : ''
              }`}
              style={{ pointerEvents: 'auto' }}
            >
              <img
                src={user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName)}&background=4F6660&color=fff`}
                alt={user.displayName}
                className="w-8 h-8 rounded-full"
                onError={(e) => {
                  const initials = (user.displayName || 'U').split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
                  (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=4F6660&color=fff&size=64`;
                }}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {user.displayName}
                </div>
                {user.companyName && (
                  <div className="text-xs text-gray-500 truncate">
                    {user.companyName}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

